import { useLazyQuery, useMutation } from "@apollo/client/react"; import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TabIngreso from './tabs/TabIngreso';
import TabRevision from './tabs/TabRevision';
import TabAprobacion from './tabs/TabAprobacion';
import RechazoModal from './tabs/components/RechazoModal';
import './capaCreationContent.scss';
import AdjuntosModal from './tabs/components/AdjuntosModal';
import toast from 'react-hot-toast';
import moment from 'moment';

import CREATE_CAPA, { adaptAddGestionCapaInput } from '../../../data/graphQL/mutations/Capa/CreateCapa';
import { useDebounce } from '../../../hooks/useDebounce';
import GET_CAPA_DETALLE, { adaptCapaCompleto, adaptDelegacionCargadaCapa } from '../../../data/graphQL/queries/Capas/getCapaDetalle';
import UPDATE_CAPA, { adaptEditGestionCapaInput } from '../../../data/graphQL/mutations/Capa/UpdateCapa';
import SUBIR_DOCUMENTOS_CAPA, { adaptArchivosArrayInput } from '../../../data/graphQL/mutations/Capa/AdjuntarArchivos';
import GET_ADJUNTOS_CAPA, { adaptAdjuntosCapaArray } from '../../../data/graphQL/queries/Capas/getAdjuntosCapa';
import HistorialModal from './tabs/components/HistorialModal';
import { PasswordModal } from './tabs/components/PasswordModal';
import TabsCalidad, { PanelsCalidad } from "../../ReclamoCreation/components/TabsCalidad";

const idUsuarioLoggeado = sessionStorage.getItem('idUsuario');
const nombreUsuarioLoggeado = `${sessionStorage.getItem('nombre')} ${sessionStorage.getItem('apellido')}`;
const PENDIENTE_CREADOR = 1;
const PENDIENTE_PRIMERA_REVISION = 2;
const PENDIENTE_SEGUNDA_REVISION = 3;
const PENDIENTE_PRORROGA = 4;
const PENDIENTE_APROBACION_FINAL = 5;
const TIPO_RECLAMO = 1;
const TIPO_RECLAMO_EFI = 4;

const ETAPA_READ_ONLY = [PENDIENTE_PRIMERA_REVISION, PENDIENTE_PRORROGA];
const etapas = {
  [PENDIENTE_CREADOR]: 0,
  [PENDIENTE_PRIMERA_REVISION]: 0,
  [PENDIENTE_SEGUNDA_REVISION]: 1,
  [PENDIENTE_PRORROGA]: 1,
  [PENDIENTE_APROBACION_FINAL]: 2
};

export const CapaCreationContent = ({ setChildrenAction, setHeaderContent }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { idCapa, isNew, idEtapa, allowedWrite, estados, idEstado } = location.state;

  const [delegacionCargada, setDelegacionCargada] = useState({});
  const [cierraCapa, setCierraCapa] = useState(false);
  const [currentTab, setCurrentTab] = useState(tabsCalidad[0]);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [capaFormData, setCapaFormData] = useState({
    stage1: {},
    stage2: {},
    stage3: {}
  });

  const formDataDebounced = useDebounce(capaFormData, 500);

  const initialTabIndex = useMemo(() => etapas[idEtapa] || 0, [idEtapa]);

  const [getCapa, { data: dataCapa, loading: loadingCapa }] = useLazyQuery(GET_CAPA_DETALLE, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataCapa || loadingCapa) return;
    if (!dataCapa?.gestionCapaById) return toast.error('No se pudo cargar la información de la capa, intentelo nuevamente más tarde.');
    setCapaFormData(adaptCapaCompleto(dataCapa?.gestionCapaById));
    setDelegacionCargada(adaptDelegacionCargadaCapa(dataCapa?.gestionCapaById));
  }, [dataCapa]);

  const [createCapa, { loading: createLoading }] = useMutation(CREATE_CAPA, {
    onCompleted: (data) => {
      if (data) {
        toast.success('Capa creado con éxito');
        navigate('/calidad/capas');
      }
    }
  });

  const [updateGestionCapa, { loading: updateLoading }] = useMutation(UPDATE_CAPA, {
    onCompleted: () => {
      toast.success('Capa actualizada con éxito.');
      navigate('/calidad/capas');
    }
  });

  const [subirArchivos, { loading: subirArchivosLoading }] = useMutation(SUBIR_DOCUMENTOS_CAPA, {
    refetchQueries: [GET_ADJUNTOS_CAPA],
    onCompleted: () => {
      toast.success(`Archivos actualizados con éxito.`);
    },
    onError: () => {
      toast.error('Error al cargar los archivos, intentelo nuevamente más tarde');
    }
  });

  const [getAdjuntos, { data: dataAdjuntos, loading: loadingAdjuntos }] = useLazyQuery(GET_ADJUNTOS_CAPA, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataAdjuntos || loadingAdjuntos) return;
    if (!dataAdjuntos?.archivosCapa) return toast.error('No se pudo cargar la información del Capa, intentelo nuevamente más tarde.');
    setCapaFormData((prev) => ({
      ...prev,
      adjuntos: adaptAdjuntosCapaArray(dataAdjuntos?.archivosCapa)
    }));
  }, [dataAdjuntos]);

  const handleStageChange = useCallback((stage, changes) => {
    setCapaFormData((prev) => ({
      ...prev,
      [`stage${stage}`]: {
        ...prev[`stage${stage}`],
        ...changes
      }
    }));
  }, []);

  // Validación específica por etapa
  const validateStage = useCallback(() => {
    const stage = `stage${initialTabIndex + 1}`;
    const stageData = capaFormData[stage];
    const errorMessages = [];

    // Funciones de validación por stage
    const validateStage1 = (data) => {
      const errors = [];

      const requiredFields = [
        { value: data.usuarioCreador, message: 'No se pudo cargar el usuario, intentelo nuevamente.\n' },
        { value: data.idDelegacion, message: 'Debe elegir una delegación.\n' },
        { value: data.idArea, message: 'Debe elegir un área.\n' },
        { value: data.idFuente, message: 'Debe elegir una fuente.\n' },
        { value: data.descripcion?.trim(), message: 'Debe ingresar una descripción.\n' },
        { value: data.accion?.trim(), message: 'Debe ingresar una acción.\n' },
        { value: data.idResponsableAccion, message: 'Debe ingresar un responsable de acción.\n' }
      ];

      requiredFields.forEach(field => {
        if (!field.value) errors.push(field.message);
      });

      // Validaciones condicionales de fuente
      if (data.idFuente === TIPO_RECLAMO && !data.idGestionReclamo) {
        errors.push('Debe elegir un Nro de Reclamo.\n');
      }
      if (data.idFuente === TIPO_RECLAMO_EFI && !data.idGestionReclamo) {
        errors.push('Debe elegir un Nro de EFI.\n');
      }

      // Validación de fecha de acción
      if (!data.fechaAccion || !moment(data.fechaAccion).isValid()) {
        errors.push('Debe ingresar una fecha de acción válida.\n');
      } else if (moment(data.fechaAccion).isBefore(data?.fechaApertura)) {
        errors.push('La fecha de acción debe ser mayor a la fecha de apertura.\n');
      }

      return errors;
    };

    const validateStage2 = (data) => {
      const errors = [];

      // Validar fecha de cierre o prórroga
      const tieneFechaCierre = !!data.fechaCierreAccion;
      const prorrogas = data.prorrogaCapa || [];
      const sinProrrogas = prorrogas.length === 0;
      const primeraAprobada = prorrogas.length === 1 && !!prorrogas[0].aprobada;
      const segundaAprobada = prorrogas.length === 2 && !!prorrogas[1].aprobada;

      if (!tieneFechaCierre && (sinProrrogas || primeraAprobada || segundaAprobada)) {
        errors.push('Debe ingresar una fecha de acción o una prórroga.\n');
      }

      // Validar fecha de cierre vs fecha de apertura
      const fechaCierreValida = moment(data.fechaCierreAccion).isValid();
      const fechaAperturaAnterior = moment(capaFormData.stage1?.fechaApertura).isAfter(data.fechaCierreAccion);

      if (fechaCierreValida && fechaAperturaAnterior) {
        errors.push('Debe ingresar una fecha de acción mayor a la fecha de apertura.\n');
      }

      // Validar prórrogas
      errors.push(...validateProrrogas(prorrogas, capaFormData.stage1?.fechaAccion));

      return errors;
    };

    const validateProrrogas = (prorrogas, fechaAccion) => {
      const errors = [];

      if (prorrogas?.length === 1) {
        const primeraProrroga = prorrogas[0];

        if (!primeraProrroga?.justificacion?.trim()) {
          errors.push('La primera prórroga debe tener una justificación válida (no puede estar vacía).\n');
        }
        if (moment(primeraProrroga.fechaProrroga).isBefore(fechaAccion)) {
          errors.push('La fecha de la primera prórroga debe ser mayor a la fecha de acción.\n');
        }
      } else if (prorrogas?.length === 2) {
        const segundaProrroga = prorrogas[1];
        const primeraProrroga = prorrogas[0];

        if (!segundaProrroga?.justificacion?.trim()) {
          errors.push('La segunda prórroga debe tener una justificación válida (no puede estar vacía).\n');
        }
        if (moment(segundaProrroga.fechaProrroga).isBefore(primeraProrroga.fechaProrroga)) {
          errors.push('La fecha de la segunda prórroga debe ser mayor a la fecha de la primera prórroga.\n');
        }
      }

      return errors;
    };

    const validateStage3 = (data) => {
      const errors = [];

      if (!data.fechaCierre || !moment(data.fechaCierre).isValid()) {
        errors.push('Debe ingresar una fecha de cierre válida.\n');
      }

      // Validar fecha de cierre vs fecha de apertura
      const fechaCierreValida = moment(data.fechaCierre).isValid();
      const fechaAperturaAnterior = moment(capaFormData.stage1?.fechaApertura).isAfter(data.fechaCierre);

      if (fechaCierreValida && fechaAperturaAnterior) {
        errors.push('Debe ingresar una fecha de cierre mayor o igual a la fecha de apertura.\n');
      }

      return errors;
    };

    // Ejecutar validación según el stage
    switch (stage) {
      case 'stage1':
        errorMessages.push(...validateStage1(stageData));
        break;
      case 'stage2':
        errorMessages.push(...validateStage2(stageData));
        break;
      case 'stage3':
        errorMessages.push(...validateStage3(stageData));
        break;
      default:
        errorMessages.push('No existe la etapa.');
        break;
    }

    if (errorMessages.length > 0) {
      toast.error(errorMessages.join(' '));
      return false;
    }

    return true;
  }, [formDataDebounced, initialTabIndex]);

  const onSaveAdjuntos = useCallback(async (newAdjuntos) => {
    if (isNew) {
      setCapaFormData((prev) => ({
        ...prev,
        adjuntos: newAdjuntos
      }));
    } else {
      if (!!idCapa) {
        const adaptedArchivos = adaptArchivosArrayInput(newAdjuntos.filter((a) => !a.idArchivo));
        await subirArchivos({
          variables: {
            idGestionCapa: idCapa,
            input: adaptedArchivos
          }
        });
      }
    }
  }, [idCapa, isNew]);

  const stepAllowedWrite = () => {
    return !ETAPA_READ_ONLY.includes(idEtapa);
  };

  const handleSave = useCallback(async () => {
    const stage = initialTabIndex + 1;
    const stageData = capaFormData['stage' + stage];
    if (!validateStage()) return;
    try {
      if (isNew) {
        const adaptedArchivos = adaptArchivosArrayInput(capaFormData?.adjuntos || []);
        createCapa({
          variables: {
            input: {
              idDelegacion: stageData.idDelegacion,
              idFuente: stageData.idFuente,
              idGestionReclamo: stageData.idGestionReclamo,
              idArea: stageData.idArea,
              idResponsableAccion: stageData.idResponsableAccion,
              descripcion: stageData.descripcion,
              causa: stageData.causa,
              accion: stageData.accion,
              fechaAccion: stageData.fechaAccion,
              observacion: stageData.observacion
            },
            archivos: adaptedArchivos
          }
        });
      } else {
        const adaptedUpdateCapaByStage = adaptEditGestionCapaInput(stageData, idCapa, stage, idEstado, idEtapa);
        updateGestionCapa({
          variables: {
            input: adaptedUpdateCapaByStage
          }
        });
      }
      // Proximamente servicios 
    } catch (error) {
      console.error(`Error guardando etapa ${stage}:`, error);
      throw error;
    }
  }, [formDataDebounced, initialTabIndex, isNew]);

  useEffect(() => {
    if (!idCapa || isNew) return;
    getCapa({ variables: { idGestionCapa: idCapa } });
    getAdjuntos({ variables: { idGestionCapa: idCapa } });
  }, [idCapa, isNew]);

  useEffect(() => {
    // Si la capa se encuentra en etapa 5 (Pendiente aprobacion final) termina el flujo
    let auxValue = idEtapa === PENDIENTE_APROBACION_FINAL;
    setCierraCapa(auxValue);
  }, [idEtapa]);

  useEffect(() => {
    setChildrenAction(() =>
      <div className='header-actions-calidad'>
        {idCapa > 0 && <div className='chip-estado-calidad'>
          <p>N. de CAPA: <strong>{` ${idCapa} | ${estados.find((estado) => estado.id === idEstado)?.displayName}`}</strong> </p>
        </div>}
        <div className='top-buttons top-buttons-creation'>
          <HistorialModal
            idGestionCapa={idCapa}
            estados={estados} />

          <AdjuntosModal
            idGestionCapa={idCapa}
            idUsuarioLoggeado={parseInt(idUsuarioLoggeado)}
            nombreUsuarioLoggeado={nombreUsuarioLoggeado}
            onSaveAdjuntos={onSaveAdjuntos}
            submitLoading={subirArchivosLoading}
            initialAdjuntos={capaFormData?.adjuntos}
            disabled={!allowedWrite}
            queryAdjuntos={getAdjuntos} />

          {!isNew && allowedWrite && <>
            <RechazoModal isRechazoParcial idGestionCapa={idCapa} idEtapa={idEtapa} />
            <RechazoModal idGestionCapa={idCapa} idEtapa={idEtapa} />
          </>}
          {allowedWrite && <PasswordModal
            onSubmit={handleSave}
            isLoading={createLoading || updateLoading}
            cierraFlujo={cierraCapa}
            idUsuarioLoggeado={idUsuarioLoggeado} />
          }
        </div>
      </div>
    );
    setHeaderContent(() =>
      <TabsCalidad
        tabs={tabsCalidad}
        currentTabIndex={currentTabIndex}
        initialTabIndex={initialTabIndex}
        handleChangeTab={handleChangeTab}
      />

    );
  }, [
    setChildrenAction,
    setHeaderContent,
    currentTabIndex,
    initialTabIndex,
    subirArchivosLoading,
    createLoading,
    updateLoading,
    isNew,
    idCapa,
    idEstado,
    handleSave]
  );

  //Setear 
  useEffect(() => handleChangeTab(initialTabIndex), [initialTabIndex]);

  const handleChangeTab = (tabIndex) => {
    setCurrentTabIndex(tabIndex);
    setCurrentTab(tabsCalidad[tabIndex]);
  };

  return (
    <div className='creation-general'>
      <PanelsCalidad
        tabs={tabsCalidad}
        initialTabIndex={initialTabIndex}
        etapaCreador={initialTabIndex === 0}
        delegacionCargada={delegacionCargada}
        currentTabIndex={currentTabIndex}
        capaFormData={capaFormData}
        handleStageChange={handleStageChange}
        idCapa={idCapa}
        isNew={isNew}
        allowedWrite={allowedWrite && stepAllowedWrite()}
        isCapa={true}
      />

    </div>);

};

const tabsCalidad = [
  {
    id: 1,
    name: 'Ingreso',
    component: <TabIngreso />
  },
  {
    id: 2,
    name: 'Revisión',
    component: <TabRevision />
  },
  {
    id: 3,
    name: 'Aprobación',
    component: <TabAprobacion />
  }];